import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Card, StatCard, Badge, Spinner, Empty } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { TierBadge, TierCard } from '../../components/ui/TierBadge';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useLinkWallet } from '../../hooks/useLinkWallet';
import { referralApi, type ReferralSummary, type Referee } from '../../api/referral';
import { walletApi } from '../../api/wallet';
import { fmtUsdt, fmtDate, shortenAddress, copyToClipboard } from '../../lib/format';

interface LinkedWallet {
  id: string;
  address: string;
  linkedAt: string;
}

export default function AffiliateDashboard() {
  const { user } = useAuth();
  const toast = useToast();
  const { address: connectedAddress, isConnected } = useAccount();
  const { linking, link: doLink } = useLinkWallet();

  const [summary, setSummary] = useState<ReferralSummary | null>(null);
  const [referees, setReferees] = useState<Referee[] | null>(null);
  const [wallets, setWallets] = useState<LinkedWallet[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [s, r, w] = await Promise.all([
        referralApi.me(),
        referralApi.referees(),
        walletApi.get(),
      ]);
      setSummary(s);
      setReferees(r.referees);
      setWallets(w.wallets);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load dashboard';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCopyLink = async () => {
    if (!summary?.referralUrl) return;
    const ok = await copyToClipboard(summary.referralUrl);
    if (ok) toast.success('Referral link copied');
    else toast.error('Could not copy. Select and copy manually.');
  };

  const handleLinkWallet = async () => {
    const ok = await doLink();
    if (ok) {
      toast.success('Wallet linked');
      await refresh();
    } else {
      toast.error('Wallet linking failed. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size={40} />
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="max-w-2xl mx-auto py-10">
        <Card>
          <div className="text-danger font-display text-lg">Could not load dashboard</div>
          <div className="text-ink-600 text-sm mt-2">{error}</div>
          <Button onClick={refresh} variant="outline" className="mt-4">
            Try again
          </Button>
        </Card>
      </div>
    );
  }

  const hasWallet = (wallets?.length ?? 0) > 0;
  const linkedAddress = wallets?.[0]?.address ?? null;
  const kycApproved = summary.kycStatus === 'APPROVED';

  const tier = summary.tier;
  const isAffiliate = tier.current !== 'NONE';

  // Progress percentage for the next-tier bar
  let progressPercent = 0;
  let progressLabel = '';
  if (tier.next === 'BRONZE') {
    progressPercent = Math.min(100, (summary.spend.personalUsdt / 150) * 100);
    progressLabel = `$${fmtUsdt(summary.spend.personalUsdt)} of $150 personal spend`;
  } else if (tier.next === 'SILVER') {
    progressPercent = Math.min(100, ((summary.spend.personalUsdt - 150) / (300 - 150)) * 100);
    progressLabel = `$${fmtUsdt(summary.spend.personalUsdt)} of $300 personal spend`;
  } else if (tier.next === 'FOUNDERS_CIRCLE') {
    progressPercent = Math.min(100, (summary.spend.directTeamVolumeUsdt / 10000) * 100);
    progressLabel = `$${fmtUsdt(summary.spend.directTeamVolumeUsdt)} of $10,000 team volume`;
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-violet-600 mb-2">Affiliate dashboard</div>
          <h1 className="font-display text-4xl font-bold">Welcome, {user?.email}</h1>
          <p className="text-ink-600 mt-2">
            Your referral code:{' '}
            <span className="font-mono font-semibold text-ink">{summary.referralCode}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TierBadge tier={tier.current} size="md" />
          <Badge kind={kycApproved ? 'success' : 'warn'}>KYC: {summary.kycStatus}</Badge>
        </div>
      </div>

      {/* Tier card */}
      <div className="mb-6">
        <TierCard
          tier={tier.current}
          usdtPercent={tier.rates.usdtPercent}
          aimlPercent={tier.rates.aimlPercent}
          leadershipOverridePercent={tier.rates.leadershipOverridePercent}
        >
          {/* Progress to next tier */}
          {tier.next && (
            <div className="mt-4 pt-4 border-t border-paper-200">
              <div className="flex justify-between items-center mb-2">
                <div className="text-xs text-ink-600 uppercase tracking-wider">
                  Progress to {tier.nextLabel}
                </div>
                <div className="text-xs font-mono text-ink-700">{Math.floor(progressPercent)}%</div>
              </div>
              <div className="h-2 bg-paper-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-magenta-500 via-violet-500 to-azure-500 transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="text-xs text-ink-600 mt-2">{progressLabel}</div>
            </div>
          )}
        </TierCard>
      </div>

      {/* Not-yet-affiliate banner */}
      {!isAffiliate && (
        <Card className="mb-6 !bg-violet-50 !border-violet-200">
          <div className="flex items-start gap-4">
            <div className="text-3xl">🎯</div>
            <div className="flex-1">
              <h3 className="font-display font-bold text-lg">
                Become a Bronze Affiliate
              </h3>
              <p className="text-ink-700 text-sm mt-1">
                Buy at least <strong>$150 of AIML</strong> to unlock your referral link and start
                earning <strong>15% USDT cash + 2% AIML</strong> on every friend you bring.
              </p>
              <div className="mt-4">
                <Link to="/buy">
                  <Button variant="gradient" size="sm">
                    Buy $AIML now
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Referral link */}
      {isAffiliate && summary.referralUrl && (
        <Card className="mb-6">
          <div className="eyebrow mb-3">Your referral link</div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-0 font-mono text-sm bg-paper-100 px-4 py-3 rounded-2xl border border-paper-300 truncate">
              {summary.referralUrl}
            </div>
            <Button onClick={handleCopyLink} variant="primary">
              Copy link
            </Button>
          </div>
          <p className="text-xs text-ink-600 mt-3">
            Share this link. When someone buys $AIML through it, you earn{' '}
            <strong>{tier.rates.usdtPercent}% USDT</strong> + <strong>{tier.rates.aimlPercent}% AIML</strong>{' '}
            sent straight to your wallet (after KYC).
          </p>
        </Card>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Clicks"
          value={summary.stats.totalClicks}
          accent="azure"
          hint="Total link clicks"
        />
        <StatCard
          label="Signups"
          value={summary.stats.totalSignups}
          accent="violet"
          hint="People who joined"
        />
        <StatCard
          label="Qualifying refs"
          value={summary.stats.qualifyingReferrals}
          accent="magenta"
          hint="Referees who bought"
        />
        <StatCard
          label="Payable USDT"
          value={`$${fmtUsdt(summary.earnings.payableUsdt)}`}
          accent="ink"
          hint={
            kycApproved && hasWallet
              ? 'Will be paid in next batch'
              : 'Locked until KYC + wallet'
          }
        />
      </div>

      {/* Earnings breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card>
          <div className="eyebrow mb-2">Total earned</div>
          <div className="stat-num text-ink">
            ${fmtUsdt(summary.earnings.totalCommissionEarnedUsdt)}
          </div>
          <div className="text-xs text-ink-600 mt-1">Lifetime commissions</div>
        </Card>
        <Card>
          <div className="eyebrow mb-2">Already paid</div>
          <div className="stat-num text-success">
            ${fmtUsdt(summary.earnings.totalCommissionPaidUsdt)}
          </div>
          <div className="text-xs text-ink-600 mt-1">Sent to your wallet</div>
        </Card>
        <Card>
          <div className="eyebrow mb-2">Awaiting payout</div>
          <div className="stat-num text-magenta-500">
            ${fmtUsdt(summary.earnings.payableUsdt)}
          </div>
          <div className="text-xs text-ink-600 mt-1">In next batch</div>
        </Card>
      </div>

      {/* Founders Circle leadership earnings (only shown if any) */}
      {summary.earnings.totalLeadershipOverrideEarnedUsdt > 0 && (
        <Card className="mb-6 !border-violet-300 !bg-gradient-to-br from-violet-50 to-magenta-50">
          <div className="flex items-center justify-between">
            <div>
              <div className="eyebrow text-violet-700">Leadership override earned</div>
              <div className="stat-num text-gradient">
                ${fmtUsdt(summary.earnings.totalLeadershipOverrideEarnedUsdt)}
              </div>
              <div className="text-xs text-ink-700 mt-1">
                3% USDT bonus on your team's indirect sales (Founders Circle perk)
              </div>
            </div>
            <div className="text-4xl">🥇</div>
          </div>
        </Card>
      )}

      {/* KYC + wallet status row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card>
          <div className="flex items-start justify-between mb-3">
            <div className="eyebrow">KYC verification</div>
            <Badge
              kind={
                summary.kycStatus === 'APPROVED'
                  ? 'success'
                  : summary.kycStatus === 'PENDING'
                    ? 'warn'
                    : summary.kycStatus === 'REJECTED'
                      ? 'danger'
                      : 'neutral'
              }
            >
              {summary.kycStatus}
            </Badge>
          </div>
          {summary.kycStatus === 'NONE' && (
            <>
              <p className="text-ink-700 text-sm">
                You must complete KYC before any commissions can be paid out.
              </p>
              <Link to="/dashboard/kyc" className="inline-block mt-4">
                <Button variant="primary" size="sm">
                  Start KYC
                </Button>
              </Link>
            </>
          )}
          {summary.kycStatus === 'PENDING' && (
            <p className="text-ink-700 text-sm">
              Your KYC is under review. We'll email you when it's decided.
            </p>
          )}
          {summary.kycStatus === 'APPROVED' && (
            <p className="text-success text-sm">✓ KYC approved. Payouts can be processed.</p>
          )}
          {summary.kycStatus === 'REJECTED' && (
            <>
              <p className="text-danger text-sm">
                Your KYC was rejected. You may resubmit.
              </p>
              <Link to="/dashboard/kyc" className="inline-block mt-4">
                <Button variant="primary" size="sm">
                  Resubmit KYC
                </Button>
              </Link>
            </>
          )}
        </Card>

        <Card>
          <div className="flex items-start justify-between mb-3">
            <div className="eyebrow">Payout wallet</div>
            <Badge kind={hasWallet ? 'success' : 'warn'}>
              {hasWallet ? 'Linked' : 'Not linked'}
            </Badge>
          </div>

          {hasWallet ? (
            <>
              <p className="text-ink-700 text-sm">Payouts will be sent to:</p>
              <div className="font-mono text-sm mt-2 bg-paper-100 px-3 py-2 rounded-xl">
                {linkedAddress}
              </div>
              <p className="text-xs text-ink-600 mt-2">
                Linked {wallets?.[0] && fmtDate(wallets[0].linkedAt)}
              </p>
            </>
          ) : (
            <>
              <p className="text-ink-700 text-sm">
                Link a wallet so we know where to send your USDT commissions.
              </p>
              {!isConnected ? (
                <div className="mt-4">
                  <ConnectButton showBalance={false} chainStatus="none" />
                </div>
              ) : (
                <>
                  <div className="text-xs text-ink-600 mt-3">
                    Connected: <span className="font-mono">{shortenAddress(connectedAddress)}</span>
                  </div>
                  <Button
                    onClick={handleLinkWallet}
                    isLoading={linking}
                    variant="primary"
                    size="sm"
                    className="mt-4"
                  >
                    Sign & link this wallet
                  </Button>
                </>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Referees table */}
      <div className="mb-4">
        <h2 className="font-display text-2xl font-bold">Your referrals</h2>
        <p className="text-ink-600 text-sm mt-1">
          People who signed up using your code
        </p>
      </div>

      {!referees || referees.length === 0 ? (
        <Empty
          title="No referrals yet"
          hint="Share your referral link to start earning"
        />
      ) : (
        <Card className="!p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-paper-100">
                <tr className="text-left">
                  <th className="px-4 py-3 eyebrow">Email</th>
                  <th className="px-4 py-3 eyebrow">Joined</th>
                  <th className="px-4 py-3 eyebrow">KYC</th>
                  <th className="px-4 py-3 eyebrow text-right">Purchases</th>
                  <th className="px-4 py-3 eyebrow text-right">Total spent</th>
                  <th className="px-4 py-3 eyebrow text-right">You earned</th>
                </tr>
              </thead>
              <tbody>
                {referees.map((r) => (
                  <tr key={r.id} className="border-t border-paper-200">
                    <td className="px-4 py-3 font-mono text-xs">{r.email}</td>
                    <td className="px-4 py-3 text-ink-700">{fmtDate(r.joinedAt)}</td>
                    <td className="px-4 py-3">
                      <Badge
                        kind={
                          r.kycStatus === 'APPROVED'
                            ? 'success'
                            : r.kycStatus === 'PENDING'
                              ? 'warn'
                              : 'neutral'
                        }
                      >
                        {r.kycStatus}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {r.qualifyingPurchases}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      ${fmtUsdt(r.totalPurchasedUsdt)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-success">
                      ${fmtUsdt(r.youEarnedUsdt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="mt-10 text-center text-xs text-ink-600">
        <p>
          Need help? Reach out at{' '}
          <a href="mailto:support@altcoinsmoney.com" className="text-violet-600 hover:underline">
            support@altcoinsmoney.com
          </a>
        </p>
      </div>
    </div>
  );
}
